import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import {
    AwbState,
    EVENT_DATE_TIME_OPTIONS,
    EVENT_LOCATION_OPTIONS,
    STATE_NAME_OPTIONS
} from '../types/awbState';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

// Convert options to dropdown format
const stateNameOptions = STATE_NAME_OPTIONS.map(name => ({
  value: name,
  label: name
}));

const eventDateTimeOptions = EVENT_DATE_TIME_OPTIONS.map(name => ({
  value: name,
  label: name
}));

const eventLocationOptions = EVENT_LOCATION_OPTIONS.map(name => ({
  value: name,
  label: name
}));

const AwbStateManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAwbState, setEditingAwbState] = useState<AwbState | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch AWB states with server-side filtering and pagination
  const {
    data: awbStatesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'awbStates',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/awbState?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('AwbState API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const awbStates = awbStatesResponse?.data || [];
  const pagination = awbStatesResponse?.pagination;

  // Create mutation
  const createAwbStateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/awbState', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['awbStates'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'awbState'],
      });
      toast({
        title: 'Success',
        description: 'AWB State created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create AWB state';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateAwbStateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/awbState/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['awbStates'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'awbState'],
      });
      toast({
        title: 'Success',
        description: 'AWB State updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingAwbState(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update AWB state';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteAwbStateMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/awbState/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['awbStates'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'awbState'],
      });
      toast({
        title: 'Success',
        description: 'AWB State deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete AWB state',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration
  const columns: TableColumn<AwbState>[] = [
    {
      key: 'stateName',
      label: 'State Name',
      sortable: true,
      render: (_, awbState) => (
        <div className="font-medium text-sm">
          <Badge variant="outline" className="font-normal">
            {awbState.stateName}
          </Badge>
        </div>
      ),
    },
    {
      key: 'eventDateTime',
      label: 'Event Date & Time',
      sortable: true,
      render: (_, awbState) => (
        <div className="text-sm text-muted-foreground">
          {awbState.eventDateTime}
        </div>
      ),
    },
    {
      key: 'eventLocation',
      label: 'Event Location',
      sortable: true,
      render: (_, awbState) => (
        <div className="text-sm">
          {awbState.eventLocation}
        </div>
      ),
    },
    {
      key: 'eventDescription',
      label: 'Description',
      sortable: false,
      hideOnTablet: true,
      render: (_, awbState) => (
        <div className="text-sm text-muted-foreground max-w-xs truncate">
          {awbState.eventDescription}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, awbState) => (
        <Badge
          variant={awbState.status === 'Active' ? 'default' : 'secondary'}
          className={
            awbState.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {awbState.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, awbState) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(awbState.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
    {
      key: 'eventLocation',
      label: 'Event Location',
      type: 'select',
      options: [
        { value: 'all', label: 'All Locations' },
        ...eventLocationOptions,
      ],
      placeholder: 'Filter by location',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'AWB State Configuration',
      description: 'Configure AWB state mappings for tracking events.',
      fields: [
        {
          name: 'stateName',
          label: 'State Name',
          type: 'select',
          required: true,
          placeholder: 'Select a state name',
          description: 'Select the AWB state from the predefined list',
          options: stateNameOptions,
          validation: z
            .string()
            .min(1, 'State name is required'),
        },
        {
          name: 'eventDateTime',
          label: 'Event Date & Time',
          type: 'select',
          required: true,
          placeholder: 'Select event date & time type',
          description: 'Select which date/time to use for this state',
          options: eventDateTimeOptions,
          validation: z
            .string()
            .min(1, 'Event date & time is required'),
        },
        {
          name: 'eventLocation',
          label: 'Event Location',
          type: 'select',
          required: true,
          placeholder: 'Select event location',
          description: 'Select which location to use for this state',
          options: eventLocationOptions,
          validation: z
            .string()
            .min(1, 'Event location is required'),
        },
        {
          name: 'eventDescription',
          label: 'Event Description',
          type: 'textarea',
          required: true,
          placeholder: 'Enter event description (e.g., "Shipment picked up from shipper")',
          description: 'Description to be displayed for this event',
          validation: z
            .string()
            .min(2, 'Event description must be at least 2 characters')
            .max(500, 'Event description cannot exceed 500 characters'),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this AWB state',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingAwbState(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (awbState: AwbState) => {
    setEditingAwbState(awbState);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (awbState: AwbState) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete AWB state "${awbState.stateName}"? This action cannot be undone.`
      )
    ) {
      deleteAwbStateMutation.mutate(awbState._id);
    }
  };

  const handleSubmit = async (data: any) => {
    if (editingAwbState) {
      updateAwbStateMutation.mutate({
        id: editingAwbState._id,
        data: data,
      });
    } else {
      createAwbStateMutation.mutate(data);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingAwbState(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'AWB State data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/awbState/export?${params.toString()}`
      );

      if (response.data.success) {
        const awbStates = response.data.data;

        const exportData = awbStates.map((state: AwbState) => ({
          'State Name': state.stateName,
          'Event Date & Time': state.eventDateTime,
          'Event Location': state.eventLocation,
          'Event Description': state.eventDescription,
          Status: state.status,
          'Created At': new Date(state.createdAt).toLocaleString(),
          'Updated At': new Date(state.updatedAt).toLocaleString(),
        }));

        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map((row: any) =>
            headers
              .map((header) => {
                const value = row[header];
                if (
                  typeof value === 'string' &&
                  (value.includes(',') || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',')
          ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `awb-states-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Success',
          description: `Exported ${awbStates.length} AWB states`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export AWB states',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            AWB State Management
          </h1>
          <p className='text-muted-foreground'>
            Configure AWB states for shipment tracking events
          </p>
        </div>
      </div>

      <MasterDataTable<AwbState>
        title='AWB States'
        description='Manage and configure AWB state mappings'
        data={awbStates}
        columns={columns}
        isLoading={isLoading}
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search AWB states by name or description...'
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingAwbState ? 'Edit AWB State' : 'Create AWB State'}
        sections={formSections}
        initialData={editingAwbState}
        onSubmit={handleSubmit}
        isLoading={
          createAwbStateMutation.isPending || updateAwbStateMutation.isPending
        }
        mode={editingAwbState ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default AwbStateManagement;


